import { useEffect, useState } from "react";
import { Link } from "wouter";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ALargeSmall, X } from "lucide-react";

const DISMISS_KEY = "recoverme:ios-pwa-text-size-hint-dismissed";

type IOSNavigator = Navigator & { standalone?: boolean };

function isIosPwa(): boolean {
  if (typeof window === "undefined" || typeof navigator === "undefined") return false;
  const nav: IOSNavigator = navigator;
  const ua = nav.userAgent || "";
  const isIos =
    /iPad|iPhone|iPod/.test(ua) ||
    (ua.includes("Mac") && nav.maxTouchPoints > 1);
  if (!isIos) return false;
  const standalone =
    (typeof window.matchMedia === "function" &&
      window.matchMedia("(display-mode: standalone)").matches) ||
    nav.standalone === true;
  return standalone;
}

export default function IosPwaTextSizeHint() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (localStorage.getItem(DISMISS_KEY)) return;
    if (!isIosPwa()) return;
    const t = setTimeout(() => setShow(true), 1500);
    return () => clearTimeout(t);
  }, []);

  if (!show) return null;

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, "1");
    } catch {
      /* ignore */
    }
    setShow(false);
  };

  return (
    <div
      className="fixed left-1/2 -translate-x-1/2 z-50 w-[min(28rem,calc(100vw-2rem))]"
      style={{
        top: "max(0.75rem, calc(env(safe-area-inset-top) + 0.5rem))",
      }}
      data-testid="hint-ios-pwa-text-size"
    >
      <Card className="p-3 shadow-lg flex items-start gap-3">
        <ALargeSmall className="w-5 h-5 text-primary shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0 space-y-2">
          <p className="text-sm font-medium">Need bigger text?</p>
          <p className="text-xs text-muted-foreground">
            iPhone's system text-size doesn't reach apps installed to your home
            screen. ForgeRestore has its own text-size control in Settings →
            Appearance.
          </p>
          <div className="flex gap-2 flex-wrap">
            <Button asChild size="sm" onClick={dismiss}>
              <Link href="/settings" data-testid="link-ios-hint-open-settings">
                Open Settings
              </Link>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={dismiss}
              data-testid="button-ios-hint-dismiss"
            >
              Got it
            </Button>
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={dismiss}
          aria-label="Dismiss"
          data-testid="button-ios-hint-close"
        >
          <X className="w-4 h-4" />
        </Button>
      </Card>
    </div>
  );
}
